<script setup lang="ts">
/**
 * Right-rail pane that pins one cited source for deep verification.
 * ADR 0018 Phase D.
 *
 * Driven by `sourcePane` reactive state — when a `[N]` citation in
 * `MessageBubble` is clicked, the pane fetches
 * `GET /api/chunks/{id}/context?width=2` to load the cited chunk plus
 * ±2 neighbouring chunks from the same parent doc. The centre chunk
 * is rendered with an accent border so the user can see exactly which
 * span the LLM cited; before/after chunks fade in around it.
 *
 * Clicking another citation swaps content. The × button + the Esc
 * key close the pane.
 */
import DOMPurify from "dompurify";
import { marked } from "marked";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

import { type ChunkContext, getChunkContext } from "../api/conversations";
import { sourcePane } from "../stores/source-pane";
import { displaySectionTitle, SYNTHESIS_SECTION } from "../types/chat";
import { highlightAnswerOverlap } from "../utils/citation-highlight";
import ProfileCard from "./ProfileCard.vue";

const props = defineProps<{ devUser: string; assistantSlug?: string }>();

const ctx = ref<ChunkContext | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);

/** Fetch context whenever the pinned chunk changes. The store
 * de-dupes same-chunk re-pins so this watcher only fires on a real
 * source switch. */
watch(
  () => sourcePane.state.pinned?.chunkId,
  async (chunkId) => {
    if (!chunkId) {
      ctx.value = null;
      error.value = null;
      return;
    }
    loading.value = true;
    error.value = null;
    try {
      const fetched = await getChunkContext(
        chunkId,
        2,
        props.devUser,
        props.assistantSlug,
      );
      if (!fetched) {
        error.value = "Could not load source context.";
        ctx.value = null;
      } else if (sourcePane.state.pinned?.chunkId === chunkId) {
        // Guard against a race: by the time the fetch returns, the
        // user may already have pinned a different source. Drop the
        // stale response.
        ctx.value = fetched;
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e);
      ctx.value = null;
    } finally {
      loading.value = false;
    }
  },
  { immediate: true },
);

function onEscape(e: KeyboardEvent) {
  if (e.key === "Escape" && sourcePane.state.pinned) {
    sourcePane.clear();
  }
}
onMounted(() => document.addEventListener("keydown", onEscape));
onBeforeUnmount(() => document.removeEventListener("keydown", onEscape));

const sectionDisplay = computed(() =>
  ctx.value?.centre ? displaySectionTitle(ctx.value.centre.section_title) : "",
);

const isSynthesis = computed(
  () => ctx.value?.centre?.section_title === SYNTHESIS_SECTION,
);

/** Profile docs from the kompetence_matrix / cv_files connectors
 * carry a `source` prefix of `profile:` (set by the connector — see
 * packages/content-sources/.../kompetence_matrix.py:_to_doc). When
 * the pinned source is a profile, render a structured ProfileCard
 * instead of the standard chunk-preview <pre>. The before/after
 * neighbour chunks stay raw — those are typically status mails or
 * tickets, not profile docs. */
const isProfile = computed(() =>
  (ctx.value?.doc_source || "").startsWith("profile:"),
);

/** Chunk bodies are markdown — the ingest pipeline emits tables for
 * tabular source material. Rendering them in a <pre> showed a standards
 * appendix as a wall of pipes, which is exactly the content a reader most
 * needs to scan. Parse to HTML and sanitise, same as the answer bubble. */
function renderChunk(md: string | null | undefined): string {
  return DOMPurify.sanitize(
    marked.parse(md ?? "", { async: false, gfm: true }) as string,
  );
}

/** The cited chunk, with the passages the answer drew on marked.
 *
 * The highlight pass runs over a detached element AFTER sanitisation, so
 * it can only wrap existing text nodes in <mark> — it cannot introduce
 * markup and cannot reach anything DOMPurify stripped. When no answer is
 * available the chunk renders unmarked rather than guessing. */
const centreHtml = computed(() => {
  const html = renderChunk(ctx.value?.centre?.content);
  const answer = sourcePane.state.pinned?.answerText;
  if (!answer) return html;
  const scratch = document.createElement("div");
  scratch.innerHTML = html;
  highlightAnswerOverlap(scratch, answer);
  return scratch.innerHTML;
});
</script>

<template>
  <aside class="source-pane" role="complementary" aria-label="source preview">
    <header class="source-pane__header">
      <button
        type="button"
        class="source-pane__close"
        aria-label="close source preview"
        @click="sourcePane.clear()"
      >
        ×
      </button>
      <div class="source-pane__title-stack">
        <div class="source-pane__title">
          {{ ctx?.doc_title || sourcePane.state.pinned?.hit.title || "Source" }}
        </div>
        <div class="source-pane__subtitle">
          <span
            v-if="sectionDisplay"
            :class="{ 'source-pane__synth-badge': isSynthesis }"
          >
            {{ sectionDisplay }}
          </span>
          <a
            v-if="ctx?.doc_url"
            :href="ctx.doc_url"
            target="_blank"
            rel="noreferrer"
            class="source-pane__open"
            >open ↗</a
          >
        </div>
      </div>
    </header>

    <div v-if="loading" class="source-pane__loading">loading source…</div>
    <div v-else-if="error" class="source-pane__error">{{ error }}</div>

    <div v-else-if="ctx?.centre" class="source-pane__body">
      <article
        v-for="chunk in ctx.before"
        :key="chunk.id"
        class="source-pane__chunk source-pane__chunk--neighbour"
      >
        <header
          v-if="displaySectionTitle(chunk.section_title)"
          class="source-pane__chunk-section"
        >
          {{ displaySectionTitle(chunk.section_title) }}
        </header>
        <!-- eslint-disable-next-line vue/no-v-html -- sanitised in renderChunk -->
        <div
          class="source-pane__content source-pane__content--md"
          v-html="renderChunk(chunk.content)"
        ></div>
      </article>

      <article class="source-pane__chunk source-pane__chunk--centre">
        <header class="source-pane__chunk-section">
          <span class="source-pane__centre-marker">▸ cited</span>
          {{ sectionDisplay || (isProfile ? "profile" : "") }}
        </header>
        <!-- Profile-shaped cited chunk → render the structured
             ProfileCard (Booking Matcher, CBM port Phase 3c).
             Everything else falls through to the standard <pre>
             chunk render. -->
        <ProfileCard v-if="isProfile" :chunk="ctx.centre" />
        <!-- eslint-disable-next-line vue/no-v-html -- sanitised above -->
        <div
          v-else
          class="source-pane__content source-pane__content--md"
          v-html="centreHtml"
        ></div>
        <footer
          v-if="ctx.centre.cites?.length || ctx.centre.topics?.length"
          class="source-pane__chunk-foot"
        >
          <div v-if="ctx.centre.cites?.length" class="source-pane__related">
            <span class="source-pane__foot-label">related</span>
            <code v-for="c in ctx.centre.cites" :key="c">{{ c }}</code>
          </div>
          <div v-if="ctx.centre.topics?.length" class="source-pane__topics">
            <span class="source-pane__foot-label">topics</span>
            <code v-for="t in ctx.centre.topics" :key="t">{{ t }}</code>
          </div>
        </footer>
      </article>

      <article
        v-for="chunk in ctx.after"
        :key="chunk.id"
        class="source-pane__chunk source-pane__chunk--neighbour"
      >
        <header
          v-if="displaySectionTitle(chunk.section_title)"
          class="source-pane__chunk-section"
        >
          {{ displaySectionTitle(chunk.section_title) }}
        </header>
        <!-- eslint-disable-next-line vue/no-v-html -- sanitised in renderChunk -->
        <div
          class="source-pane__content source-pane__content--md"
          v-html="renderChunk(chunk.content)"
        ></div>
      </article>
    </div>

    <div v-else class="source-pane__loading">No content for this citation.</div>
  </aside>
</template>

<style scoped>
.source-pane {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--color-surface);
  border-left: 1px solid var(--color-border);
}
.source-pane__header {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-3);
  border-bottom: 1px solid var(--color-border);
  background: var(--color-surface-alt, var(--color-surface));
  position: sticky;
  top: 0;
  z-index: 1;
}
.source-pane__close {
  font-size: 1.2rem;
  line-height: 1;
  padding: 0 0.4rem;
  background: transparent;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  cursor: pointer;
  color: var(--color-muted);
}
.source-pane__close:hover {
  color: var(--color-ink);
  background: var(--color-surface);
}
.source-pane__title-stack {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}
.source-pane__title {
  font-weight: 600;
  color: var(--color-ink);
  font-size: 0.95rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.source-pane__subtitle {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: 0.8rem;
  color: var(--color-muted);
}
.source-pane__synth-badge {
  background: var(--color-accent-dim);
  color: var(--color-accent);
  padding: 0.05em 0.4em;
  border-radius: var(--radius-sm);
  font-weight: 500;
}
.source-pane__open {
  margin-left: auto;
  color: var(--color-accent);
  text-decoration: none;
}
.source-pane__open:hover {
  text-decoration: underline;
}
.source-pane__loading,
.source-pane__error {
  padding: var(--space-3);
  color: var(--color-muted);
  font-style: italic;
}
.source-pane__error {
  color: var(--color-error, var(--color-muted));
}
.source-pane__body {
  flex: 1;
  overflow-y: auto;
  padding: var(--space-3);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.source-pane__chunk {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
}
.source-pane__chunk--neighbour {
  opacity: 0.7;
}
.source-pane__chunk--neighbour:hover {
  opacity: 1;
}
.source-pane__chunk--centre {
  border-color: var(--color-accent);
  border-width: 2px;
  box-shadow: 0 0 0 1px var(--color-accent) inset;
}
.source-pane__chunk-section {
  padding: var(--space-2) var(--space-3);
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-muted);
  border-bottom: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  gap: var(--space-2);
}
.source-pane__centre-marker {
  color: var(--color-accent);
  font-weight: 600;
  letter-spacing: normal;
  text-transform: none;
}
.source-pane__content {
  margin: 0;
  padding: var(--space-3);
  font-family: var(--font-mono, monospace);
  font-size: 0.8rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
  color: var(--color-ink);
}

/* Markdown-rendered chunk. Drops the monospace/pre-wrap of the raw view —
   the whole point is that a table reads as a table. */
.source-pane__content--md {
  font-family: inherit;
  white-space: normal;
}

.source-pane__content--md :where(p, ul, ol, table) {
  margin: 0 0 var(--space-2);
}
.source-pane__content--md :where(p, ul, ol, table):last-child {
  margin-bottom: 0;
}

/* A standards appendix is far wider than the rail. Scroll the table, never
   the pane — a horizontally scrolling rail breaks the chat layout. */
.source-pane__content--md :where(table) {
  display: block;
  overflow-x: auto;
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
}
.source-pane__content--md :where(th, td) {
  border: 1px solid var(--color-rule, rgba(0, 0, 0, 0.12));
  padding: var(--space-1) var(--space-2);
  text-align: left;
  vertical-align: top;
}
.source-pane__content--md :where(th) {
  background: var(--color-surface-sunken, rgba(0, 0, 0, 0.04));
  font-weight: 600;
}
.source-pane__content--md :where(code) {
  font-family: var(--font-mono, monospace);
  font-size: 0.95em;
}

/* The passages the answer actually drew on. Deliberately quiet — this is a
   reading aid for verification, not a warning. */
.source-pane__content--md :deep(mark.cite-match),
.source-pane__content--md mark.cite-match {
  background: var(--color-accent-wash, rgba(255, 214, 102, 0.42));
  color: inherit;
  padding: 0.05em 0.15em;
  border-radius: 2px;
}
.source-pane__chunk-foot {
  padding: var(--space-2) var(--space-3);
  border-top: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  font-size: 0.75rem;
}
.source-pane__related,
.source-pane__topics {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
}
.source-pane__foot-label {
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.65rem;
  color: var(--color-muted);
  margin-right: 0.25rem;
}
.source-pane__chunk-foot code {
  font-size: 0.8em;
  background: var(--color-surface-alt);
  padding: 0.05em 0.4em;
  border-radius: var(--radius-sm);
  color: var(--color-muted);
}
</style>
